<template>
    <div class="flex border-t border-b -mb-px border-gray-200 bg-white draggable">

        <!-- Draggable handle. -->
        <div class="w-6 flex-shrink-0 self-start py-1.5">
            <div class="w-5 h-5 flex items-center">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move handle"></i>
            </div>
        </div>

        <!-- Reference. -->
        <div class="w-16 flex">
            <estimates-old-page-cell
                class="text-sm -my-px px-2 py-1.5 border border-gray-200"
                :placeholder="item.ref"
                v-model="reference"
                @input="$store.save()"
            ></estimates-old-page-cell>
        </div>

        <!-- Description. -->
        <div class="flex-1 flex">
            <div
                v-if="estimate.cannot_be_edited_by_authed_user"
                class="whitespace-pre-line text-sm w-full -ml-px -my-px px-2 py-1.5 border border-gray-200 shadow-none relative hover:z-10 focus:z-20 resize-none"
                v-text="item.description"
            ></div>
            <autoresizable-textarea v-else :height="el => (el.scrollHeight + 2) + 'px'">
                <textarea
                    placeholder="Description..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-200 shadow-none relative hover:z-10 focus:z-20 resize-none"
                    type="text"
                    ref="description"
                    v-model="item.description"
                    @input="$store.save()"
                    rows="1"
                ></textarea>
            </autoresizable-textarea>
        </div>

        <!-- Quantity. -->
        <div class="w-20 -ml-px flex">
            <estimates-old-page-cell
                class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-200"
                placeholder="Qty"
                v-model="item.quantity"
                @change="save"
            ></estimates-old-page-cell>
        </div>

        <!-- Units of measure. -->
        <div class="w-20 -ml-px flex">
            <estimates-old-page-cell
                class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-200"
                placeholder="Uom"
                v-model="item.uom"
                @input="$store.save()"
            ></estimates-old-page-cell>
        </div>

        <!-- Rate. -->
        <div class="w-28 -ml-px flex">
            <ui-dropdown placement="bottom-start" menu-class="p-3 w-52 flex" class="flex w-full" trigger-class="flex w-full">
                <template #default="{ open, close }">
                    <estimates-old-page-cell
                        currency
                        class="text-sm text-right -ml-px -my-px px-2 py-1.5 border border-gray-200"
                        placeholder="Rate"
                        v-model="item.cf_rate"
                        @change="saveCustomerFacing('rate')"
                        @mouseenter="open"
                        @mouseleave="close"
                    ></estimates-old-page-cell>
                </template>
                <template #menu>
                    <table class="w-full">
                        <tbody>
                            <tr v-if="item.is_group_item">
                                <td class="text-gray-600 text-sm">Labour rates</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ specsDetails.rates.labour_rates | toCurrency }}
                                </td>
                            </tr>
                            <tr v-if="item.is_group_item">
                                <td class="text-gray-600 text-sm">Materials</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ specsDetails.rates.materials | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Total cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ item.rate | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Markup</td>
                                <td
                                    class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                    :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                >
                                    {{ item.markup_rate | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">%</td>
                                <td
                                    class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                    :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                >
                                    {{ item.markup_pc }}%
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ item.cf_rate | toCurrency }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </ui-dropdown>
        </div>

        <!-- Total. -->
        <div class="w-28 -ml-px flex">
            <ui-dropdown placement="bottom-start" menu-class="p-3 w-52 flex" class="flex w-full" trigger-class="flex w-full">
                <template #default="{ open, close }">
                    <estimates-old-page-cell
                        currency
                        class="text-sm text-right -ml-px -my-px px-2 py-1.5 border border-gray-200"
                        placeholder="Total"
                        v-model="item.cf_amount"
                        @change="saveCustomerFacing('amount')"
                        @mouseenter="open"
                        @mouseleave="close"
                    ></estimates-old-page-cell>
                </template>
                <template #menu>
                    <table class="w-full">
                        <tbody>
                            <tr v-if="item.is_group_item">
                                <td class="text-gray-600 text-sm">Labour rates</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ specsDetails.amounts.labour_rates | toCurrency }}
                                </td>
                            </tr>
                            <tr v-if="item.is_group_item">
                                <td class="text-gray-600 text-sm">Materials</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ specsDetails.amounts.materials | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Total cost</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ item.amount | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Markup</td>
                                <td
                                    class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                    :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                >
                                    {{ item.markup_amount | toCurrency }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">%</td>
                                <td
                                    class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                    :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                >
                                    {{ item.markup_pc }}%
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    <div class="border-b border-gray-200 my-1"></div>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-gray-600 text-sm">Total</td>
                                <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                    {{ item.cf_amount | toCurrency }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </ui-dropdown>
        </div>

        <!-- Specs. -->
        <button
            @click="$modals.open('estimates-old-specs-item-sidepage', { item, specsDetails })"
            class="w-20 -ml-px px-2 py-1.5 border-r border-gray-200 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 hover:z-10 focus:z-20 relative flex items-start"
        >
            <div class="h-5 flex -space-x-1">
                <estimates-old-specs-icon v-for="spec in specsToDisplay" :key="spec.id" :spec="spec" class="ring-2 ring-white" :square="item.group_use_custom_quantities"></estimates-old-specs-icon>
            </div>
        </button>

        <!-- Dropdown. -->
        <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
            <ui-dropdown menu-class="p-3 w-48">
                <template #default="{ toggle }">
                    <button @click="toggle" class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                        <i class="fa fa-ellipsis-h fa-fw"></i>
                    </button>
                </template>
                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-hammer" @click="$modals.open('estimates-old-specs-item-sidepage', { item })">Build Item</ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-calculator" @click="$modals.open('estimates-old-page-task-item-price-modal', { item, specsDetails })">Price Item</ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-clipboard-list" @click="$modals.open('estimates-old-page-material-list-sidepage', { context: item, contextType: 'item' }); close();">See material list</ui-dropdown-item>
                    <ui-dropdown-item v-if="! estimate.cannot_be_edited_by_authed_user" icon="fas fa-copy" @click="$store.duplicateItem(task, item); close();">Duplicate</ui-dropdown-item>
                    <ui-dropdown-item v-if="! estimate.cannot_be_edited_by_authed_user" icon="fas fa-trash" danger @click="deleteItem">Delete</ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>
    </div>
</template>

<script>
import { estimateStore } from '@stores'

export default {
    props: {
        task: Object,
        item: Object,
    },
    data () {
        return {
            forceCustomRef: false,
            rateFocused: false,
            totalFocused: false,
        }
    },
    computed: {
        $store () {
            return estimateStore
        },
        estimate () {
            return this.$store.state.estimate
        },
        uniqueSpecs () {
            const localSpecIds = this.item.group.map(itemSpec => itemSpec.spec_id)
            if (localSpecIds.length === 0) return []
            return this.$store.state.localSpecs.filter(spec => localSpecIds.includes(spec.id))
        },
        specsToDisplay () {
            return this.uniqueSpecs.slice(0, 3)
        },
        specsDetails () {
            return this.$store.getItemGroupDetails(this.item)
        },
        hasNonDefaultMarkup () {
            return this.item.source_of_truth_markup !== 'default'
                || this.item.computation_strategy === 'markup'
        },
        reference: {
            get () {
                return this.forceCustomRef || this.item.custom_ref
                    ? this.item.custom_ref
                    : this.item.ref
            },
            set (newValue) {
                if (newValue == this.item.ref) {
                    this.forceCustomRef = false
                    this.item.custom_ref = ''
                } else {
                    this.forceCustomRef = true
                    this.item.custom_ref = newValue
                }
            },
        },
    },
    methods: {
        deleteItem () {
            this.$modals.confirmDelete({
                title: `Delete item ${this.item.ref}`,
                button: 'Yes, delete item',
                handle: () => this.$store.deleteItem(this.task, this.item)
            })
        },
        saveCustomerFacing (sourceOfTruth) {
            this.item.source_of_truth_cf = sourceOfTruth
            if (this.item.is_group_item) {
                this.item.computation_strategy = 'markup'
            } else if (this.item.computation_strategy === 'cf') {
                this.item.computation_strategy = 'cost'
            }
            this.save()
        },
        save () {
            this.$store.recalculateItem(this.item, true)
            this.$store.save()
        },
    },
}
</script>
